"use client";

import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { Avatar, AvatarFallback, AvatarImage } from "@bcc/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bcc/components/ui/dropdown-menu";
import { useOptionalTour } from "@bcc/contexts/tour-context";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { COUNTRIES_WITH_LOCALES, REGIONS_WITH_LOCALES, normalizeVendorCountryCode } from "@client-shared/lib/constants";
import { Vendor } from "@client-shared/providers";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import type { VendorCountryCode } from "@shared/schemas";
import type { Language, Locale } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import { HelpCircle, LogOut, Settings, Store } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CountryFlag } from "./products/products-table/cells/CountryFlag";
import { Button } from "./ui";
import {
  setUserNavEmailTestId,
  setUserNavLanguageButtonTestId,
  USER_NAV_MULTILINGUAL_ATTRIBUTE,
  setUserNavLanguageSwitcherTestId,
  setUserNavTestId,
  setUserNavVendorNameTestId,
} from "./user-nav.utils";

interface UserNavProps {
  readonly testId?: string;
}

interface AvailableLanguage {
  locale: Locale;
  name: Language;
  countryCode: VendorCountryCode;
}

export function UserNav({ testId }: UserNavProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vendorState, vendorDispatch, vendorUtils] = Vendor.useVendorContext();
  const { currentVendor } = vendorState;
  const [authState] = useSFDCAuthState();
  const queryClient = useQueryClient();
  const tour = useOptionalTour();

  const { firstName, lastName, email, vendorName: currentVendorName } = currentVendor ?? {};

  const fallBackProfileName = useMemo(
    () =>
      [firstName, lastName]
        .filter((name): name is string => Boolean(name))
        .map((name) => name.charAt(0).toUpperCase())
        .join(" "),
    [firstName, lastName]
  );

  const vendorName = useMemo(() => {
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    return currentVendorName ?? (fullName || email) ?? "";
  }, [currentVendorName, firstName, lastName, email]);

  const vendorInitialsForAdminRoaster = useMemo(() => {
    const names = currentVendorName?.split(" ") ?? [];
    const firstInitial = names[0]?.charAt(0).toUpperCase() ?? "";
    const secondInitial = names[1]?.charAt(0).toUpperCase() ?? "";
    return `${firstInitial}${secondInitial}`;
  }, [currentVendorName]);

  const availableLanguages = useMemo<AvailableLanguage[]>(() => {
    const countryCode = normalizeVendorCountryCode(currentVendor?.countryCode);
    if (countryCode) {
      // Every FlagCountryCode is also a VendorCountryCode, so the row's own flag needs no fallback.
      return COUNTRIES_WITH_LOCALES[countryCode].map((language) => ({ ...language, countryCode: language.flagCountryCode }));
    }

    const region = currentVendor?.region;
    if (!region) {
      return [];
    }
    if (!(region in REGIONS_WITH_LOCALES)) {
      return [];
    }
    return REGIONS_WITH_LOCALES[region as keyof typeof REGIONS_WITH_LOCALES].map((language) => ({
      ...language,
      countryCode: normalizeVendorCountryCode(language.flagCountryCode) ?? "GB",
    }));
  }, [currentVendor?.countryCode, currentVendor?.region]);

  const handleSwitchRoaster = useCallback(() => {
    queryClient.clear();
    vendorUtils?.clearVendorState();
    navigate(ROUTE_PATHS.ADMIN_ROASTERS);
  }, [queryClient, vendorUtils, navigate]);

  const handleLogout = useCallback(() => {
    navigate(ROUTE_PATHS.LOGOUT);
  }, [navigate]);

  if (!currentVendor) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild testId={testId ?? setUserNavTestId("trigger")}>
        <Button
          className="relative h-9 w-9 rounded-full cursor-pointer outline-none"
          aria-label={t("userNav.userMenu")}
          data-tour-id={TOUR_IDS.profile.userNav}
          {...{ [USER_NAV_MULTILINGUAL_ATTRIBUTE]: availableLanguages.length > 1 ? "true" : undefined }}
        >
          <Avatar className="h-9 w-9">
            <AvatarImage src="" alt={vendorName} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {authState.isRoasterAdmin ? vendorInitialsForAdminRoaster : fallBackProfileName}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent testId={setUserNavTestId("content")} className="w-[16.8rem]" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p data-testid={setUserNavVendorNameTestId()} className="text-sm font-medium leading-none">
              {vendorName}
            </p>
            <p data-testid={setUserNavEmailTestId()} className="text-xs leading-none text-muted-foreground">
              {email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid={setUserNavTestId("preferences-item")} onClick={() => navigate(ROUTE_PATHS.PREFERENCES)}>
          <Settings className="mr-2 h-4 w-4" />
          <span>{t("userNav.preferences")}</span>
        </DropdownMenuItem>
        {(tour?.isTourGuideEnabled || tour?.isFeatureTourGuideEnabled) && (
          <DropdownMenuItem
            data-testid={setUserNavTestId("start-tour-item")}
            onClick={() => (tour.setupGuidePage ? tour.startTour({ mode: "onboarding" }) : tour.startTour())}
          >
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>{t(tour.setupGuidePage ? "tour.menu.setupGuide" : "tour.menu.startTour")}</span>
          </DropdownMenuItem>
        )}
        {authState.isRoasterAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid={setUserNavTestId("switch-roaster-item")} onClick={handleSwitchRoaster}>
              <Store className="mr-2 h-4 w-4" />
              <span>{t("userNav.switchRoaster")}</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        {availableLanguages.length > 1 ? (
          <>
            <DropdownMenuLabel
              data-testid={setUserNavLanguageSwitcherTestId("label")}
              className="text-xs font-medium text-muted-foreground"
            >
              {t("userNav.language")}
            </DropdownMenuLabel>
            {availableLanguages.map(({ locale, name, countryCode }) => (
              <DropdownMenuItem
                key={locale}
                onClick={() => vendorDispatch(Vendor.setCurrentLocale(locale as Locale))}
                data-testid={setUserNavLanguageButtonTestId(locale)}
                aria-label={t(`userNav.languages.${name}`)}
                title={t(`userNav.languages.${name}`)}
                className="cursor-pointer gap-2"
              >
                <CountryFlag countryCode={countryCode} size={20} className="shrink-0" alt={t(`userNav.flagAlt.${countryCode}`)} />
                <span>{t(`userNav.languages.${name}`)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem data-testid={setUserNavTestId("logout-item")} className="cursor-pointer text-destructive" onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t("userNav.logOut")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
